import { useMutation } from '@tanstack/react-query';

import axios from 'axios';
import { MutationConfig, queryClient } from 'lib/react-query';

import { Snapshot, UpdateSnapshotDto } from 'types';
import { convertSnapshot } from 'api/dtoConverters';

export const updateSnapshot = async ({
  data,
  snapshotId,
}: {
  data: UpdateSnapshotDto;
  snapshotId: number;
}): Promise<Snapshot> => {
  const response = await axios.put(
    `/api/snapshots/${snapshotId}`,
    convertSnapshot(data)
  );
  return response.data as Snapshot;
};

type UseUpdateSnapshotOptions = {
  config?: MutationConfig<typeof updateSnapshot>;
};

export const useUpdateSnapshot = ({ config }: UseUpdateSnapshotOptions) => {
  return useMutation({
    onMutate: async (updatingSnapshot) => {
      await queryClient.cancelQueries([
        'snapshot',
        updatingSnapshot?.snapshotId,
      ]);

      const previousSnapshot = queryClient.getQueryData<Snapshot>([
        'snapshot',
        updatingSnapshot?.snapshotId,
      ]);

      queryClient.setQueryData(['snapshot', updatingSnapshot?.snapshotId], {
        ...previousSnapshot,
        ...updatingSnapshot.data,
        id: updatingSnapshot.snapshotId,
      });

      return previousSnapshot;
    },
    onError: (_, __, context: any) => {
      if (context?.previousSnapshot) {
        queryClient.setQueryData(
          ['snapshot', context.previousSnapshot.id],
          context.previousSnapshots
        );
      }
    },
    onSuccess: (data: Snapshot) => {
      queryClient.invalidateQueries(['snapshot', data.id]);
      queryClient.invalidateQueries(['snapshots', data.analyzer_id]);
      queryClient.invalidateQueries(['snapshots', 'list']);
    },
    ...config,
    mutationFn: updateSnapshot,
  });
};
